import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Laptop } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { api, ensureOk, unwrap, type StaffProfile } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { useMe } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga } from '@/components/Estados';
import { Campo } from '@/features/auth/AuthLayout';
import { fechaHora } from '@/lib/formato';

type Sesion = components['schemas']['StaffSession'];

const perfilSchema = z.object({
  name: z.string().trim().min(2, 'Escribe tu nombre').max(100),
  startUrl: z
    .string()
    .trim()
    .max(250)
    .regex(/^(\/[A-Za-z0-9/_\-?=&.]*)?$/, 'Debe ser una ruta del panel, por ejemplo /alumnos'),
});

const claveSchema = z
  .object({
    currentPassword: z.string().min(1, 'Escribe tu contraseña actual'),
    newPassword: z.string().min(10, 'Usa al menos 10 caracteres'),
    confirmar: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmar, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmar'],
  });

function Perfil({ me }: { me: StaffProfile }) {
  const qc = useQueryClient();
  const form = useForm<z.infer<typeof perfilSchema>>({
    resolver: zodResolver(perfilSchema),
    defaultValues: { name: me.name, startUrl: me.startUrl ?? '' },
  });
  const guardar = useMutation({
    mutationFn: (v: z.infer<typeof perfilSchema>) =>
      unwrap<StaffProfile>(
        api.PATCH('/api/v1/admin/me', {
          body: { name: v.name, startUrl: v.startUrl === '' ? null : v.startUrl },
        }),
      ),
    onSuccess: (p) => {
      qc.setQueryData(['me'], p);
      toast.success('Datos actualizados.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const { errors } = form.formState;
  return (
    <section className="panel-vidrio space-y-4">
      <h2 className="text-lg font-extrabold">Datos</h2>
      <form
        className="space-y-4"
        onSubmit={(e) => void form.handleSubmit((v) => guardar.mutate(v))(e)}
        noValidate
      >
        <Campo etiqueta="Nombre" error={errors.name?.message}>
          <input className="input-vidrio" {...form.register('name')} />
        </Campo>
        <Campo etiqueta="Correo">
          <input className="input-vidrio" value={me.email} disabled readOnly />
        </Campo>
        <Campo etiqueta="Página de inicio (opcional)" error={errors.startUrl?.message}>
          <input className="input-vidrio" placeholder="/alumnos" {...form.register('startUrl')} />
        </Campo>
        <button type="submit" className="btn-cs btn-primario" disabled={guardar.isPending}>
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </form>
    </section>
  );
}

function Contrasena() {
  const qc = useQueryClient();
  const form = useForm<z.infer<typeof claveSchema>>({ resolver: zodResolver(claveSchema) });
  const cambiar = useMutation({
    mutationFn: (v: z.infer<typeof claveSchema>) =>
      ensureOk(
        api.PUT('/api/v1/admin/me/password', {
          body: { currentPassword: v.currentPassword, newPassword: v.newPassword },
        }),
      ),
    onSuccess: () => {
      form.reset();
      void qc.invalidateQueries({ queryKey: ['me', 'sesiones'] });
      toast.success('Contraseña actualizada. Cerramos tus otras sesiones.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const { errors } = form.formState;
  return (
    <section className="panel-vidrio space-y-4">
      <h2 className="text-lg font-extrabold">Contraseña</h2>
      <form
        className="space-y-4"
        onSubmit={(e) => void form.handleSubmit((v) => cambiar.mutate(v))(e)}
        noValidate
      >
        <Campo etiqueta="Contraseña actual" error={errors.currentPassword?.message}>
          <input
            type="password"
            autoComplete="current-password"
            className="input-vidrio"
            {...form.register('currentPassword')}
          />
        </Campo>
        <Campo etiqueta="Nueva contraseña" error={errors.newPassword?.message}>
          <input
            type="password"
            autoComplete="new-password"
            className="input-vidrio"
            {...form.register('newPassword')}
          />
        </Campo>
        <Campo etiqueta="Confirma la nueva contraseña" error={errors.confirmar?.message}>
          <input
            type="password"
            autoComplete="new-password"
            className="input-vidrio"
            {...form.register('confirmar')}
          />
        </Campo>
        <button type="submit" className="btn-cs btn-primario" disabled={cambiar.isPending}>
          {cambiar.isPending ? 'Guardando…' : 'Cambiar contraseña'}
        </button>
      </form>
    </section>
  );
}

function Sesiones() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me', 'sesiones'],
    queryFn: () => unwrap<Sesion[]>(api.GET('/api/v1/admin/me/sessions')),
  });
  const cerrar = useMutation({
    mutationFn: (id: string) =>
      ensureOk(api.DELETE('/api/v1/admin/me/sessions/{id}', { params: { path: { id } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me', 'sesiones'] }),
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <section className="panel-vidrio space-y-3 lg:col-span-2">
      <h2 className="text-lg font-extrabold">Sesiones abiertas</h2>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      <ul className="divide-y divide-[var(--separador)]">
        {q.data?.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-3 py-3">
            <Laptop size={20} aria-hidden className="text-texto-3" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">
                {s.userAgent ?? 'Navegador'}{' '}
                {s.current && <span className="insignia insignia-activa ml-1">Esta sesión</span>}
              </p>
              <p className="text-xs text-texto-3">
                IP {s.ip ?? '—'} · última actividad {fechaHora(s.lastSeenAt)} · vence{' '}
                {fechaHora(s.expiresAt)}
              </p>
            </div>
            {!s.current && (
              <button
                type="button"
                className="btn-cs btn-contorno"
                disabled={cerrar.isPending}
                onClick={() => cerrar.mutate(s.id)}
              >
                Cerrar
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CuentaPage() {
  const { data: me, isPending, error, refetch } = useMe();
  return (
    <>
      <Encabezado titulo="Mi cuenta" />
      {isPending && <Cargando />}
      {error && <ErrorCarga error={error} onReintentar={() => void refetch()} />}
      {me && (
        <div className="grid gap-6 lg:grid-cols-2">
          <Perfil me={me} />
          <Contrasena />
          <Sesiones />
        </div>
      )}
    </>
  );
}
